/**
 * Resolvable value expressions as Foundry evaluates them: `@path` terms replaced from roll data
 * (`Roll.replaceFormulaData`) and the result evaluated as arithmetic with the system's Math
 * helpers (`Roll.safeEval` plus `ternary`, `gte`, `when`, `match`, `btwn`, ...).
 */

export type FormulaValue = number | boolean | null;

export class FormulaError extends Error {}

const TERM_PATTERN = /@([a-z.0-9_-]+)/gi;

/** Replace every `@path` with the value the lookup returns. Unresolved terms are left in place and listed. */
export function replaceFormulaData(formula: string, lookup: (path: string) => unknown): { formula: string; unresolved: string[] } {
  const unresolved: string[] = [];
  const replaced = formula.replace(TERM_PATTERN, (match, path: string) => {
    const value = lookup(path);
    if (value === undefined) {
      unresolved.push(match);
      return match;
    }
    if (value === null) return "null";
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "string") return value;
    return String(value);
  });
  return { formula: replaced, unresolved };
}

type Token =
  | { kind: "number"; value: number }
  | { kind: "ident"; value: string }
  | { kind: "op"; value: string }
  | { kind: "end" };

const OPERATORS = ["===", "!==", "==", "!=", ">=", "<=", "&&", "||", ">", "<", "+", "-", "*", "/", "%", "(", ")", ",", "?", ":", "!"];

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const ch = source[i]!;
    if (/\s/.test(ch)) { i++; continue; }
    if (/[0-9.]/.test(ch)) {
      const m = /^\d*\.?\d+(?:e[+-]?\d+)?/i.exec(source.slice(i));
      if (!m) throw new FormulaError(`unexpected character "${ch}" at ${i}`);
      tokens.push({ kind: "number", value: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      const m = /^[A-Za-z_$][\w$]*/.exec(source.slice(i))!;
      tokens.push({ kind: "ident", value: m[0] });
      i += m[0].length;
      continue;
    }
    const op = OPERATORS.find((o) => source.startsWith(o, i));
    if (!op) throw new FormulaError(`unexpected character "${ch}" at ${i}`);
    tokens.push({ kind: "op", value: op });
    i += op.length;
  }
  tokens.push({ kind: "end" });
  return tokens;
}

type Fn = (...args: FormulaValue[]) => FormulaValue;

const num = (v: FormulaValue): number => Number(v);

/** Math helpers available unprefixed, as Foundry exposes `Math` plus the pf2e additions. */
const FUNCTIONS: Record<string, Fn> = {
  abs: (a) => Math.abs(num(a)),
  ceil: (a) => Math.ceil(num(a)),
  floor: (a) => Math.floor(num(a)),
  round: (a) => Math.round(num(a)),
  trunc: (a) => Math.trunc(num(a)),
  sign: (a) => Math.sign(num(a)),
  sqrt: (a) => Math.sqrt(num(a)),
  pow: (a, b) => Math.pow(num(a), num(b)),
  max: (...args) => Math.max(...args.map(num)),
  min: (...args) => Math.min(...args.map(num)),
  clamp: (v, lo, hi) => Math.min(Math.max(num(v), num(lo)), num(hi)),
  btwn: (v, gte, lte) => num(v) >= num(gte) && num(v) <= num(lte),
  eq: (a, b) => a === b,
  ne: (a, b) => a !== b,
  gt: (a, b) => num(a) > num(b),
  gte: (a, b) => num(a) >= num(b),
  lt: (a, b) => num(a) < num(b),
  lte: (a, b) => num(a) <= num(b),
  ternary: (condition, ifTrue, ifFalse) => (condition ? (ifTrue ?? null) : (ifFalse ?? null)),
  match: (...args) => args.find((a) => a !== null && a !== undefined) ?? 0,
  when: (condition, then) => (condition ? (then ?? null) : null),
};

const LITERALS: Record<string, FormulaValue> = { true: true, false: false, null: null, Infinity: Infinity, NaN: NaN };

class Parser {
  #tokens: Token[];
  #pos = 0;

  constructor(tokens: Token[]) {
    this.#tokens = tokens;
  }

  parse(): FormulaValue {
    const value = this.#ternary();
    if (this.#peek().kind !== "end") throw new FormulaError(`unexpected token after expression`);
    return value;
  }

  #peek(): Token {
    return this.#tokens[this.#pos]!;
  }

  #next(): Token {
    return this.#tokens[this.#pos++]!;
  }

  #isOp(value: string): boolean {
    const t = this.#peek();
    return t.kind === "op" && t.value === value;
  }

  #expectOp(value: string): void {
    if (!this.#isOp(value)) throw new FormulaError(`expected "${value}"`);
    this.#next();
  }

  #ternary(): FormulaValue {
    const condition = this.#or();
    if (this.#isOp("?")) {
      this.#next();
      const a = this.#ternary();
      this.#expectOp(":");
      const b = this.#ternary();
      return condition ? a : b;
    }
    return condition;
  }

  #or(): FormulaValue {
    let left = this.#and();
    while (this.#isOp("||")) { this.#next(); const right = this.#and(); left = left || right; }
    return left;
  }

  #and(): FormulaValue {
    let left = this.#equality();
    while (this.#isOp("&&")) { this.#next(); const right = this.#equality(); left = left && right; }
    return left;
  }

  #equality(): FormulaValue {
    let left = this.#comparison();
    for (;;) {
      if (this.#isOp("===") || this.#isOp("==")) { this.#next(); left = left === this.#comparison(); }
      else if (this.#isOp("!==") || this.#isOp("!=")) { this.#next(); left = left !== this.#comparison(); }
      else return left;
    }
  }

  #comparison(): FormulaValue {
    let left = this.#additive();
    for (;;) {
      if (this.#isOp(">=")) { this.#next(); left = num(left) >= num(this.#additive()); }
      else if (this.#isOp("<=")) { this.#next(); left = num(left) <= num(this.#additive()); }
      else if (this.#isOp(">")) { this.#next(); left = num(left) > num(this.#additive()); }
      else if (this.#isOp("<")) { this.#next(); left = num(left) < num(this.#additive()); }
      else return left;
    }
  }

  #additive(): FormulaValue {
    let left = this.#multiplicative();
    for (;;) {
      if (this.#isOp("+")) { this.#next(); left = num(left) + num(this.#multiplicative()); }
      else if (this.#isOp("-")) { this.#next(); left = num(left) - num(this.#multiplicative()); }
      else return left;
    }
  }

  #multiplicative(): FormulaValue {
    let left = this.#unary();
    for (;;) {
      if (this.#isOp("*")) { this.#next(); left = num(left) * num(this.#unary()); }
      else if (this.#isOp("/")) { this.#next(); left = num(left) / num(this.#unary()); }
      else if (this.#isOp("%")) { this.#next(); left = num(left) % num(this.#unary()); }
      else return left;
    }
  }

  #unary(): FormulaValue {
    if (this.#isOp("-")) { this.#next(); return -num(this.#unary()); }
    if (this.#isOp("+")) { this.#next(); return num(this.#unary()); }
    if (this.#isOp("!")) { this.#next(); return !this.#unary(); }
    return this.#primary();
  }

  #primary(): FormulaValue {
    const token = this.#next();
    if (token.kind === "number") return token.value;
    if (token.kind === "op" && token.value === "(") {
      const value = this.#ternary();
      this.#expectOp(")");
      return value;
    }
    if (token.kind === "ident") {
      if (this.#isOp("(")) {
        this.#next();
        const args: FormulaValue[] = [];
        if (!this.#isOp(")")) {
          args.push(this.#ternary());
          while (this.#isOp(",")) { this.#next(); args.push(this.#ternary()); }
        }
        this.#expectOp(")");
        const name = token.value.startsWith("Math.") ? token.value.slice(5) : token.value;
        const fn = FUNCTIONS[name];
        if (!fn) throw new FormulaError(`unknown function "${token.value}"`);
        return fn(...args);
      }
      if (Object.hasOwn(LITERALS, token.value)) return LITERALS[token.value]!;
      throw new FormulaError(`unknown identifier "${token.value}"`);
    }
    throw new FormulaError(token.kind === "end" ? "unexpected end of formula" : `unexpected "${token.value}"`);
  }
}

/** Evaluate a formula with no remaining `@` terms. Throws FormulaError on malformed input. */
export function evaluateFormula(source: string): FormulaValue {
  // Foundry's tokenizer lets "Math.floor" through as an identifier; fold the prefix in.
  const tokens = tokenize(source.replace(/\bMath\.([a-z]+)/g, "$1"));
  return new Parser(tokens).parse();
}
